import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import Text from "../../../shared/ui/Typography/Text";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { fetchClientShares, deleteClientShare } from "../api/shares";
import { messageFromError } from "../../../shared/api/drfErrors";

import ClientShareCreateModal from "./ClientShareCreateModal";
import ClientShareRevokeConfirmModal from "./ClientShareRevokeConfirmModal";

// ClientSharesCard — Phase B3 owner-side sidebar card for the
// "Поділено з" section of design/clientDetail.html (lines 657-675).
//
// Topology class: R8 §4 T-2 (section-owned) + T-3 (parent-header
// presentation alias). Sibling of [[ClientContactsSection]],
// [[ClientActivitySection]], [[ClientTasksSection]] — but rendered
// in the sidebar instead of the main column. The visual envelope
// reuses the sidebar's __cd-card primitive (sibling of
// [[ClientNotesCard]] and [[ClientStampsCard]]); the section-loader
// pattern reuses the main-column section's load/error/refresh
// vocabulary inline (per CLAUDE.md anti-abstraction rules —
// healthy duplication, R8 T-7).
//
// Owns:
//   * its own /shares/ fetch (owner-only per B2 F-F)
//   * its own load/error/refresh state
//   * the create-share modal lifecycle (open / close / refetch)
//   * the revoke-share confirmation lifecycle (target row / busy /
//     error / refetch)
//
// Does NOT own:
//   * client core (parent owns it)
//   * any coordination with other sections
//   * any cross-screen freshness (no mayBeStale emit — revocation
//     is section-local; the recipient discovers loss via their
//     own next 404 per B2 F-L / R6-14 / R8 C-1)
//
// Owner gating: This card MUST only be rendered when
// `client.is_owner === true`. The backend's IsClientOwnerOnly tier
// applies to all four share endpoints (B2 F-F); rendering this
// surface to a non-owner would issue requests that 403. The
// gating check is performed by the parent
// [[ClientDetailScreenContent]] at render time; this widget
// double-checks via the `client` prop's `is_owner` flag.
//
// T-3 bridge (R8 §4 T-3 + I2 §"R7 §6.4" proposal):
//
//   exposes exactly ONE imperative method via forwardRef +
//   useImperativeHandle:
//
//       openShareModal()  — void, no args, no async, no return
//                           payload, fire-and-forget.
//
//   The parent ([[ClientDetailScreenContent]]) holds a ref to this
//   card and may call this method from its header's "Поділитися"
//   button. The parent observes nothing back: it does not know
//   whether the modal opened, whether the form was submitted,
//   whether a share was created, or whether the card refetched.
//
//   Bridge narrowness (A-1 §6.4 audit):
//     (1) one method per ref               — only openShareModal
//     (2) void return                       — returns undefined
//     (3) no async awaited from caller      — handler does not await
//     (4) no cross-section invocation       — parent calls one ref only
//     (5) no registry shape                 — single named method
//     (6) deletable                         — the card ALSO renders
//         its own "+" trigger in __cd-card-head; the parent's alias
//         is purely additive. Removing the ref + parent button
//         does not remove the entry point.
//
// Render rule (per Phase B3 brief):
//   * shared_with.fullname  — required, from UserShortSerializer.
//   * can_edit              — purely presentation: "Може редагувати"
//                             vs "Лише перегляд". No collaborator /
//                             member / delegate / role taxonomy.
//   * expires_at            — rendered ONLY when present, formatted
//                             "до DD.MM.YYYY". No "expiring soon",
//                             no countdown, no warning.
//   * is_active_now         — when false, applies a single
//                             __share-row--expired modifier. Active
//                             rows render without that modifier.
//                             Backend computes the predicate (B2 F-I).
//   * note, created_at      — NOT rendered. They live on the row
//                             but the design's sidebar narrative
//                             does not surface them; rendering them
//                             here would inflate the card. Per the
//                             brief: render ONLY what the design
//                             puts on this surface.

const unwrapListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

// Format expires_at strictly. No "expiring soon" / no countdown /
// no severity warning (per Phase B3 EXPIRY RULE).
const formatExpiresAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}.${mo}.${yyyy}`;
};

// Permission label — purely presentation mapping from can_edit.
// Brief ROLE LABEL RULE: only these two strings are allowed.
const permissionLabel = (canEdit) =>
  canEdit ? "Може редагувати" : "Лише перегляд";

const permissionVariant = (canEdit) => (canEdit ? "editor" : "readonly");

const recipientInitials = (recipient) => {
  if (!recipient) return "?";
  const fullname = (recipient.fullname || "").trim();
  if (fullname.length > 0) {
    const parts = fullname.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0] || "").join("").toUpperCase() || "?";
  }
  const handle = (recipient.handle || "").trim();
  if (handle.length > 0) return handle[0].toUpperCase();
  return "?";
};

const recipientLabel = (recipient) => {
  if (!recipient) return "Невідомий рієлтор";
  const fullname = (recipient.fullname || "").trim();
  if (fullname.length > 0) return fullname;
  const handle = (recipient.handle || "").trim();
  if (handle.length > 0) return handle;
  return "Невідомий рієлтор";
};

// Phase S12 — D3 R2-c avatar color identity restoration.
// Deterministic palette selector for sidebar share-row sm avatars.
// Same palette + same modulo logic intentionally duplicated in
// [[ClientDetailScreenContent]] (N=2 — below R7 §7.3 three-caller
// extraction floor; CLAUDE.md anti-abstraction discipline). Palette
// order MUST stay in lockstep with the parent-header copy; drift
// would render the same person in different colors across surfaces.
const AVATAR_COLORS = ["blue", "green", "orange", "purple", "rose", "teal"];
const avatarColorClass = (seed) => {
  const n = Number(seed);
  const idx = Number.isFinite(n) ? Math.abs(n) % AVATAR_COLORS.length : 0;
  return `avatar--${AVATAR_COLORS[idx]}`;
};

function ClientSharesCard({ client, clientId }, ref) {
  const [shares, setShares] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  // Section-owned T-2 modal state (R8 §4 T-2). Local useState only —
  // the parent ClientDetailScreenContent is not informed when this
  // toggles; the parent only knows how to ASK us to open it.
  const [createOpen, setCreateOpen] = useState(false);

  // Section-owned T-6 destructive confirmation state. Single target
  // at a time — clicking a row's revoke X populates revokeTarget;
  // confirming runs the DELETE; the section then refetches and
  // clears the target.
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState(null);

  // R8.7 B-1 — Closure-race guard for destructive double-click. Read
  // synchronously inside handleRevokeConfirm; flipped before any
  // await. The useState `revoking` above remains the source of truth
  // for the disabled prop on the modal confirm button — the ref ONLY
  // closes the gap between the first click and the next render where
  // the state-derived disable applies.
  const revokingRef = useRef(false);

  // T-3 narrow bridge. ONE method. void. fire-and-forget.
  // See header docstring for the §6.4 axis-by-axis justification.
  useImperativeHandle(
    ref,
    () => ({
      openShareModal() {
        setCreateOpen(true);
      },
    }),
    []
  );

  useEffect(() => {
    if (clientId == null) return undefined;

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchClientShares(clientId)
      .then((data) => {
        if (cancelled) return;
        setShares(unwrapListPayload(data));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        // 403 here means client.is_owner flag was stale (someone
        // else became owner mid-session). The card will render the
        // error and the parent's next refetch of client core will
        // re-evaluate is_owner. No proactive eviction.
        if (status === 403) {
          setLoadError("Лише власник може переглядати список доступів.");
        } else {
          setLoadError(messageFromError(err));
        }
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleReload = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const data = await fetchClientShares(clientId);
      setShares(unwrapListPayload(data));
      setLoadState("loaded");
    } catch (err) {
      setRefreshError(messageFromError(err));
    } finally {
      setRefreshing(false);
    }
  };

  const handleRevokeRequest = (share) => {
    setRevokeError(null);
    setRevokeTarget(share);
  };

  const handleRevokeCancel = () => {
    if (revoking) return;
    setRevokeTarget(null);
    setRevokeError(null);
  };

  const handleRevokeConfirm = async () => {
    // R8.7 B-1 — synchronous ref guard closes the rapid-double-click
    // window before React re-renders the state-derived disable.
    if (revokingRef.current || !revokeTarget) return;
    revokingRef.current = true;
    setRevoking(true);
    setRevokeError(null);
    try {
      await deleteClientShare(clientId, revokeTarget.id);
      // Section-local refetch — no cross-screen mayBeStale, no
      // optimistic eviction. The list updates when the backend
      // reflects the deletion.
      const data = await fetchClientShares(clientId);
      setShares(unwrapListPayload(data));
      setRevokeTarget(null);
    } catch (err) {
      setRevokeError(messageFromError(err));
    } finally {
      revokingRef.current = false;
      setRevoking(false);
    }
  };

  if (!client || client.is_owner !== true) {
    return null;
  }

  const list = shares;
  const headingId = "client-detail-shares-heading";

  return (
    <article
      className="cd-card"
      aria-labelledby={headingId}
    >
      <header className="cd-card__head">
        <h3 id={headingId}>
          <i className="fa-solid fa-share-nodes" aria-hidden="true" />
          <span>Поділено з</span>
        </h3>
        <div className="feature-client-detail__cd-card-actions">
          <button
            type="button"
            className="cd-section__edit"
            onClick={() => setCreateOpen(true)}
            aria-label="Поділитися клієнтом"
            title="Поділитися"
          >
            <i className="fa-solid fa-plus" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="feature-client-detail__refresh-icon-btn"
            onClick={handleReload}
            disabled={refreshing || loadState === "loading"}
            data-loading={refreshing ? "true" : undefined}
            aria-label="Оновити список доступів"
            title="Оновити"
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <i
                className="fa-solid fa-arrows-rotate"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </header>

      {refreshError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-client-detail__section-state">
          <Spinner size="sm" label="Завантаження доступів" />
        </div>
      ) : loadState === "error" ? (
        <div className="feature-client-detail__section-state">
          <Text as="p" size="sm" tone="danger">
            {loadError || "Не вдалося завантажити доступи."}
          </Text>
        </div>
      ) : list.length === 0 ? (
        <Text as="p" size="sm" tone="secondary">
          Доступи ще не надавалися.
        </Text>
      ) : (
        <ul className="feature-client-detail__share-list">
          {list.map((share) => {
            const recipient = share.shared_with;
            const name = recipientLabel(recipient);
            const initials = recipientInitials(recipient);
            const canEdit = share.can_edit === true;
            const permLabel = permissionLabel(canEdit);
            const permVariant = permissionVariant(canEdit);
            const expiresLabel = share.expires_at
              ? `до ${formatExpiresAt(share.expires_at)}`
              : null;
            const expired = share.is_active_now === false;
            const rowClassName = expired
              ? "feature-client-detail__share-row feature-client-detail__share-row--expired"
              : "feature-client-detail__share-row";
            return (
              <li key={share.id} className={rowClassName}>
                <span
                  className={`avatar avatar--sm ${avatarColorClass(
                    recipient && recipient.id
                  )}`}
                  aria-hidden="true"
                >
                  {initials}
                </span>
                <div className="feature-client-detail__share-row-body">
                  <strong className="feature-client-detail__share-row-name">
                    {name}
                  </strong>
                  <span className="feature-client-detail__share-row-meta">
                    <span
                      className={`feature-client-detail__share-perm feature-client-detail__share-perm--${permVariant}`}
                    >
                      {permLabel}
                    </span>
                    {expiresLabel ? (
                      <span className="feature-client-detail__share-row-expiry">
                        {expiresLabel}
                      </span>
                    ) : null}
                  </span>
                </div>
                <button
                  type="button"
                  className="row-action row-action--danger"
                  onClick={() => handleRevokeRequest(share)}
                  aria-label={`Відкликати доступ у ${name}`}
                  title="Відкликати"
                  disabled={revoking}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ClientShareCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        clientId={clientId}
        onSaved={() => {
          setCreateOpen(false);
          handleReload();
        }}
      />

      <ClientShareRevokeConfirmModal
        open={revokeTarget !== null}
        recipientName={
          revokeTarget ? recipientLabel(revokeTarget.shared_with) : ""
        }
        revoking={revoking}
        errorMessage={revokeError}
        onCancel={handleRevokeCancel}
        onConfirm={handleRevokeConfirm}
      />
    </article>
  );
}

export default forwardRef(ClientSharesCard);
